import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, InfoIcon, ListCheck, Loader } from "lucide-react";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric } from "validator";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { verifyBasicGst } from "../../../Services/ApiTestService/apiTestService";

function VerifyGstBasic() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [gstNumber, setGstNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    if (!gstNumber) {
      return setInputError("Enter GST No.");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await verifyBasicGst({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        gstin: gstNumber.trim(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="col-span-2 md:col-span-6 flex justify-end">
        <Link
          to="/TestMaster"
          className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-1 py-1 rounded-md font-semibold shadow-md transition duration-300"
        >
          <span>Go Back</span>
          <IoArrowBackCircleOutline size={24} />
        </Link>
      </div>

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md sm:p-6 bg-white">
          <div className="flex items-center flex-wrap gap-4">
            {/* GST No. */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                GST No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: 29AA******8J1ZE"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end ">
              <button
                disabled={isLoading || !gstNumber}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-gradient-to-br from-blue-50/50 to-slate-50 border border-slate-200 border-l-4 border-l-blue-600 p-5 rounded-r-xl rounded-l-sm shadow-sm">
            {/* Heading */}
            <h3 className="font-bold text-slate-800 text-base flex items-center gap-2 mb-3">
              <InfoIcon className="h-5 w-5 text-blue-600 shrink-0" />
              Important Instructions
            </h3>

            {/* Structured Bullet Points */}
            <ul className="space-y-2.5 text-sm text-slate-600 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold select-none mt-0.5">
                  •
                </span>
                <span>
                  Please enter a valid, 15-digit <strong>GST number</strong>{" "}
                  into the input panel.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold select-none mt-0.5">
                  •
                </span>
                <span>
                  Click the <strong>Verify</strong> button to initiate the live
                  verification lookup.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold select-none mt-0.5">
                  •
                </span>
                <span>
                  Once processing is complete, the system will structure the
                  profile details below. You can easily review or copy this
                  information for further reference.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- BUSINESS DETAILS PROFILE -------- */}
            <div className="max-w-4xl mx-auto bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden">
              {/* Header Accent Panel */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <ListCheck className="h-6 w-6 opacity-90" />
                  <div>
                    <h2 className="text-xl font-bold tracking-wide">
                      Business Verification Details
                    </h2>
                    <p className="text-xs text-blue-100 mt-0.5">
                      Official GSTIN & Taxpayer Records
                    </p>
                  </div>
                </div>

                {/* Dynamic Status Badge */}
                {response?.data?.status && (
                  <span
                    className={`self-start sm:self-auto px-3 text-nowrap py-1 rounded-full text-xs font-bold tracking-wider uppercase shadow-sm ${
                      response.data.status.toLowerCase() === "active"
                        ? "bg-green-500/20 text-green-200 border border-green-500/30"
                        : "bg-red-500/20 text-red-200 border border-red-500/30"
                    }`}
                  >
                    ● {response.data.status}
                  </span>
                )}
              </div>

              <div className="p-6 space-y-6">
                {/* SECTION 1: Legal Identity */}
                <div className="border-b border-gray-100 pb-5">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Company Identity
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-400 block font-medium">
                        Legal Name
                      </span>
                      <span className="text-base font-bold text-gray-800">
                        {response?.data?.legal_name || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium">
                        Trade Name
                      </span>
                      <span className="text-base font-semibold text-gray-600">
                        {response?.data?.trade_name || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Tax & Registration Grid */}
                <div className="border-b border-gray-100 pb-5">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Tax & Registration Profile
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        GSTIN / UIN
                      </span>
                      <span className="font-mono bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-sm font-bold border border-blue-100 inline-block tracking-wider">
                        {response?.data?.gstin || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        PAN
                      </span>
                      <span className="font-mono bg-gray-50 text-gray-700 px-2.5 py-1 rounded-md text-sm font-semibold border border-gray-200 inline-block tracking-wider">
                        {response?.data?.pan || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        Constitution of Business
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {response?.data?.constitution || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        Taxpayer Type
                      </span>
                      <span className="text-sm font-medium text-gray-800">
                        {response?.data?.taxpayer_type || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        Registration Date
                      </span>
                      <span className="text-sm font-medium text-gray-800">
                        {response?.data?.registration_date || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block font-medium mb-1">
                        Last Updated
                      </span>
                      <span className="text-sm font-medium text-gray-500 italic">
                        {response?.data?.last_updated || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: Principal Place of Business */}
                <div className="border-b border-gray-100 pb-5">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Principal Place of Business
                  </h3>
                  <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-100">
                    <p className="text-sm text-gray-700 font-medium leading-relaxed mb-3">
                      {response?.data?.address || "N/A"}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-3 border-t border-slate-200/60 text-xs">
                      <div>
                        <span className="text-gray-400 block font-medium">
                          District
                        </span>
                        <span className="font-semibold text-gray-700">
                          {response?.data?.district || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 block font-medium">
                          State
                        </span>
                        <span className="font-semibold text-gray-700">
                          {response?.data?.state}{" "}
                          {response?.data?.state_code
                            ? `(${response.data.state_code})`
                            : ""}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 block font-medium">
                          Pincode
                        </span>
                        <span className="font-mono font-semibold text-gray-700">
                          {response?.data?.pincode || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 4: Nature of Business Activities Array */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Nature of Business Activities
                  </h3>
                  {response?.data?.nature_of_business &&
                  response.data.nature_of_business.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {response.data.nature_of_business.map(
                        (activity, index) => (
                          <span
                            key={index}
                            className="bg-indigo-50/60 text-indigo-700 border border-indigo-100/80 px-3 py-1 rounded-lg text-xs font-semibold tracking-wide shadow-sm"
                          >
                            {activity}
                          </span>
                        ),
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">
                      No business activities specified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VerifyGstBasic;
